import React, { useEffect, useMemo } from 'react';
import { Button } from '../../../../../components/UI-CHAKRA/Button';
import { Flex } from '../../../../../components/UI-CHAKRA/Flex';
import { HStack } from '../../../../../components/UI-CHAKRA/HStack';
import { PinInput } from '../../../../../components/UI-CHAKRA/PinInput';
import { PinInputField } from '../../../../../components/UI-CHAKRA/PinInputField';
import { Text } from '../../../../../components/UI-CHAKRA/Text';
import { chakra } from '@chakra-ui/react';
import { Box } from '../../../../../components/UI-CHAKRA/Box';
import { DEFAULT_PRIMARY_COLOR } from '../../../usePrescription/helpers.mock';
import { ReactComponent as SolutiLogo } from '../../../../../assets/images/certificates/birdid.svg';
import { useFormik } from 'formik';
import { sleep } from '../../../../../helpers/sleep';
import { TEST_FLOW, usePrescriptionStore } from '../../../../../stores/prescriptionStore';
import { debounce, get } from 'lodash';
import { toast } from '../../../../../helpers/toast';
import { CertificateService } from '../../../../certificate/certificateService';
import { TrackerEventService } from '../../../trackerEventService';
import { useLoading } from '../../../../../helpers/useLoading';
import { amplitudeModule } from '../../../../../shared/amplitude';

interface SolutiAuthProps {
  prescriptionId: number;
  prescriberId: number;
  onCancel(): void;
  onComplete(): void;
  testFlow: TEST_FLOW.A | TEST_FLOW.B;
}

const CustomPinInputField = chakra(PinInputField, {
  baseStyle: {
    width: '48px',
    height: '70px',
    borderRadius: '10px',
    borderColor: '#CBBFE6',
  },
});

export function SolutiAuth(props: SolutiAuthProps) {
  const { prescriptionId, prescriberId, onComplete, onCancel, testFlow } = props;

  const { setShouldHideCertificates } = usePrescriptionStore();
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const prescription = usePrescriptionStore((store) => store.prescription);
  const [loadingSession, runWithLoadingSession] = useLoading();

  const formik = useFormik({
    validateOnChange: true,
    initialValues: {
      otp: '',
      isCertifiedActive: false,
    },
    onSubmit: async (values) => {
      try {
        await CertificateService.authenticateSoluti(prescriberId, values.otp);
        formik.setFieldValue('isCertifiedActive', true);

        amplitudeModule.esignatureSuccess({
          partner_id: prescriberConfiguration?.idParceiro ?? 0,
          partner_name: prescriberConfiguration?.Nome ?? '',
          establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        });

        await TrackerEventService.track(prescriptionId, 'soluti-ab-test', {
          fluxo: testFlow,
          assinaturaDigital: true,
          idMedico: prescriberId,
        });
      } catch (e) {
        const status = get(e, 'response.status', null);

        if (status === 412) {
          formik.setFieldError('otp', 'Token incorreto.');
          return;
        }

        toast.error('Não foi possível emitir a autorização do certificado selecionado.');
      }
    },
  });

  const debouncedSubmit = useMemo(() => debounce(formik.handleSubmit, 200), [formik.handleSubmit]);

  const setFieldValue = useMemo(() => formik.setFieldValue, [formik.setFieldValue]);

  const buttonLabel = useMemo(() => {
    if (loadingSession) return 'Validando sessão';

    return formik.values.isCertifiedActive ? 'Certificado ativo' : 'Ativar Certificado';
  }, [loadingSession, formik.values.isCertifiedActive]);

  useEffect(() => {
    const run = async () => {
      if (!formik.values.isCertifiedActive) return;

      await sleep(3000);
      onComplete();
      setShouldHideCertificates(true);
    };

    run();
  }, [formik.values.isCertifiedActive, onComplete, setShouldHideCertificates]);

  useEffect(() => {
    const run = () => {
      runWithLoadingSession(async () => {
        const session = await CertificateService.checkSolutiStatus(prescriberId);

        if (!session) return;
        setFieldValue('isCertifiedActive', true);
      });
    };

    run();
  }, [prescriberId, setFieldValue, runWithLoadingSession]);

  return (
    <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
      <Box mt={'10px'}>
        <Flex gap={'10px'} flexDirection={{ base: 'column', md: 'row' }}>
          <SolutiLogo />
          <Flex flexDir={'column'} justifyContent={'center'}>
            <Text fontWeight={700} fontSize={'18px'} style={{ marginBottom: '8px' }}>
              Abra o aplicativo BirdID e digite o código de seis dígitos.
            </Text>

            {props.testFlow === TEST_FLOW.B && <Text fontSize={'14px'}>Seu certificado ficará ativo por 7 dias.</Text>}
          </Flex>
        </Flex>

        <HStack justifyContent={'center'} marginTop={'30px'}>
          <PinInput
            placeholder=""
            onChange={(val) => formik.setFieldValue('otp', val)}
            onComplete={() => debouncedSubmit()}
            isInvalid={!!formik.errors.otp}
            value={formik.values.otp}
            isDisabled={formik.values.isCertifiedActive}
          >
            <CustomPinInputField />
            <CustomPinInputField />
            <CustomPinInputField />
            <CustomPinInputField />
            <CustomPinInputField />
            <CustomPinInputField />
          </PinInput>
        </HStack>

        <Flex justifyContent={'center'} mt={'20px'}>
          <Box>
            <Button
              backgroundColor={DEFAULT_PRIMARY_COLOR}
              borderRadius={'40px'}
              padding={'20px'}
              h={'56px'}
              w={'100%'}
              color={'#fff'}
              fontSize={'14px'}
              type="submit"
              isLoading={formik.isSubmitting}
              disabled={formik.values.isCertifiedActive}
            >
              {buttonLabel}
            </Button>
            <Button
              color={DEFAULT_PRIMARY_COLOR}
              fontSize={'14px'}
              textDecoration={'underline'}
              bg={'#fff'}
              w={'100%'}
              onClick={onCancel}
              mt={'10px'}
            >
              Continuar sem ativar
            </Button>
          </Box>
        </Flex>
      </Box>
    </form>
  );
}
